import React from 'react';
import { motion } from 'framer-motion';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { Calendar, Users, Target, Phone, MapPin, Facebook, Info, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PlanYourVisitCTA } from '../components/PlanYourVisitCTA';
import { PHONE, PHONE_TEL, GOOGLE_MAPS_URL, FACEBOOK_URL } from '../constants/business';
export function Events() {
  useDocumentMeta('Shotgun Demo Day & Store Events | Kjergaard Sports', 'Shotgun Demo Day on July 18, 9:00 AM – 5:00 PM, plus Permit to Carry class updates and store events at Kjergaard Sports in Lake Benton, Minnesota.');
  return <div data-id="element-375">
      {/* HERO */}
      <section className="relative flex items-center min-h-[60vh] lg:min-h-[480px] py-20 lg:py-24 overflow-hidden" data-id="element-376">
        <div className="absolute inset-0 z-0" data-id="element-377">
          <img src="https://cdn.magicpatterns.com/uploads/uUknJmVfAFGtW8fiJmX2Gd/events1.jpg" alt="Shotgun Demo Day and store event display at Kjergaard Sports — Benelli, Browning, and Winchester tents with firearms on tables" className="w-full h-full object-cover object-center" data-id="element-378" />
          {/* Lighter left-to-right gradient so the photo reads through */}
          <div className="absolute inset-0 bg-gradient-to-r from-brand-charcoal/85 via-brand-charcoal/55 to-brand-charcoal/25" data-id="element-379"></div>
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-brand-charcoal/70 to-transparent" data-id="element-380"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-381">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7
        }} className="max-w-3xl" data-id="element-382">
            <div className="flex items-center space-x-3 mb-5" data-id="element-383">
              <div className="h-px w-10 bg-brand-leather" data-id="element-384"></div>
              <span className="text-brand-bone uppercase tracking-widest text-xs font-semibold drop-shadow-md" data-id="element-385">
                Kjergaard Sports
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-brand-offwhite leading-tight mb-5 drop-shadow-lg" data-id="element-386">
              Shotgun Demo Day &amp; Store Events
            </h1>

            <p className="text-base md:text-lg text-brand-cream leading-relaxed mb-8 max-w-2xl drop-shadow-md" data-id="element-387">
              A focused shotgun event on July 18 from 9:00 AM to 5:00 PM, plus
              class updates and store announcements throughout the year. Follow
              Kjergaard Sports on Facebook or call the store for the latest
              details.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4" data-id="element-388">
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="bg-brand-hunter hover:bg-brand-wood text-brand-offwhite px-7 py-3.5 rounded-sm font-medium text-center transition-colors flex items-center justify-center" data-id="element-389">
                <Facebook className="w-5 h-5 mr-2" data-id="element-390" />
                Follow on Facebook
              </a>
              <a href={PHONE_TEL} className="border-2 border-brand-bone hover:border-brand-leather hover:bg-brand-leather/10 text-brand-offwhite px-7 py-3.5 rounded-sm font-medium text-center transition-colors flex items-center justify-center" data-id="element-391">
                <Phone className="w-5 h-5 mr-2" data-id="element-392" />
                Call the Store
              </a>
              <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="bg-brand-offwhite/10 hover:bg-brand-offwhite/20 text-brand-offwhite px-7 py-3.5 rounded-sm font-medium text-center transition-colors flex items-center justify-center backdrop-blur-sm border border-brand-offwhite/20" data-id="element-393">
                <MapPin className="w-5 h-5 mr-2" data-id="element-394" />
                Get Directions
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* COMMUNITY & CONNECTION + EVENT CARDS */}
      <section className="py-20 bg-brand-offwhite" data-id="element-395">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-396">
          {/* Community & Connection */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20" data-id="element-397">
            <div data-id="element-398">
              <div className="flex items-center space-x-3 mb-5" data-id="element-399">
                <div className="h-px w-10 bg-brand-leather" data-id="element-400"></div>
                <span className="text-brand-hunter uppercase tracking-widest text-xs font-semibold" data-id="element-401">
                  Events, updates, and in-store support
                </span>
              </div>

              <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6 leading-tight" data-id="element-402">
                Community &amp; Connection
              </h2>
              <p className="text-lg text-brand-charcoal/80 mb-5 leading-relaxed" data-id="element-403">
                Kjergaard Sports is more than a store — it's a gathering place
                for the local shooting and hunting community. Throughout the
                year, we host events designed to bring enthusiasts together,
                showcase products, and promote safe, responsible firearm use.
              </p>
              <p className="text-base text-brand-charcoal/75 leading-relaxed" data-id="element-404">
                Class updates, manufacturer days, and seasonal announcements
                happen in person — so make the trip, call ahead, or follow along
                on Facebook for the latest.
              </p>
            </div>

            <div className="relative" data-id="element-405">
              <div className="absolute inset-0 bg-brand-sage translate-x-4 translate-y-4 rounded-sm" data-id="element-406"></div>
              <img src="https://cdn.magicpatterns.com/uploads/nBYgfXMrjN6a9xShzw47uG/events.jpg" alt="Browning and Benelli manufacturer displays set up at a Kjergaard Sports event" className="relative z-10 w-full h-72 md:h-96 rounded-sm shadow-lg object-cover" data-id="element-407" />
            </div>
          </div>

          {/* Event cards — 3 up */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12" data-id="element-408">
            {/* Card 1: Shotgun Demo Day */}
            <div className="bg-brand-bone p-10 rounded-sm border border-brand-cream flex flex-col h-full" data-id="element-409">
              <Target className="w-10 h-10 text-brand-hunter mb-6" data-id="element-410" />
              <div className="inline-flex items-center bg-brand-hunter/10 text-brand-hunter text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-sm mb-4 self-start" data-id="element-411">
                July 18 • 9:00 AM – 5:00 PM
              </div>
              <h3 className="text-2xl font-serif text-brand-charcoal mb-4" data-id="element-412">
                Shotgun Demo Day
              </h3>
              <p className="text-brand-charcoal/80 leading-relaxed mb-6 flex-grow" data-id="element-413">
                A focused shotgun event scheduled for July 18 from 9:00 AM to
                5:00 PM. Compare options in person, talk with manufacturer reps,
                and ask questions. Kjergaard Sports now hosts Shotgun Demo Day
                as a more focused event centered on shotguns, in-store
                expertise, and hands-on product information.
              </p>
              <p className="text-sm text-brand-charcoal/70 italic" data-id="element-414">
                Follow Kjergaard Sports on Facebook or call the store for the
                latest details.
              </p>
            </div>

            {/* Card 2: Store Events & Updates */}
            <div className="bg-brand-bone p-10 rounded-sm border border-brand-cream flex flex-col h-full" data-id="element-415">
              <Users className="w-10 h-10 text-brand-hunter mb-6" data-id="element-416" />
              <h3 className="text-2xl font-serif text-brand-charcoal mb-4" data-id="element-417">
                Store Events &amp; Updates
              </h3>
              <p className="text-brand-charcoal/80 leading-relaxed mb-6 flex-grow" data-id="element-418">
                Throughout the year, Kjergaard Sports shares class updates,
                manufacturer information, demo opportunities, and store
                announcements. Follow Facebook or call ahead for current
                details.
              </p>
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-brand-hunter font-medium hover:text-brand-wood transition-colors text-sm" data-id="element-419">
                <Facebook className="w-4 h-4 mr-2" data-id="element-420" />
                Follow on Facebook
              </a>
            </div>

            {/* Card 3: Permit to Carry Class Updates */}
            <div className="bg-brand-bone p-10 rounded-sm border border-brand-cream flex flex-col h-full" data-id="element-421">
              <BookOpen className="w-10 h-10 text-brand-hunter mb-6" data-id="element-422" />
              <h3 className="text-2xl font-serif text-brand-charcoal mb-4" data-id="element-423">
                Minnesota Permit to Carry Classes
              </h3>
              <p className="text-brand-charcoal/80 leading-relaxed mb-6 flex-grow" data-id="element-424">
                Classes are $100, no signup is needed, and schedules vary by
                demand. Classes are held on Saturdays. Contact Lance for current
                class information.
              </p>
              <Link to="/mn-permit-to-carry-classes" className="inline-flex items-center text-brand-hunter font-medium hover:text-brand-wood transition-colors text-sm" data-id="element-425">
                Class details &amp; contact Lance →
              </Link>
            </div>
          </div>

          {/* Supporting image strip */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-16" data-id="element-426">
            <div className="overflow-hidden rounded-sm shadow-sm group" data-id="element-427">
              <img src="https://cdn.magicpatterns.com/uploads/4MjKD2zi9iiVqWYu7BdiBh/events2.jpg" alt="Large event tent at a Kjergaard Sports community gathering" className="w-full h-44 md:h-52 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-428" />
            </div>
            <div className="overflow-hidden rounded-sm shadow-sm group" data-id="element-429">
              <img src="https://cdn.magicpatterns.com/uploads/pW5HFrMebncV4g2gQkm2kR/events3.jpg" alt="Customers browsing firearms inside a manufacturer event tent at Kjergaard Sports" className="w-full h-44 md:h-52 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-430" />
            </div>
            <div className="overflow-hidden rounded-sm shadow-sm group" data-id="element-431">
              <img src="https://cdn.magicpatterns.com/uploads/vXFtU1J5XUHFpFDTrQ4FEp/events5.jpg" alt="Beretta, Nosler, and BPI Outdoors manufacturer booths at a Kjergaard Sports event" className="w-full h-44 md:h-52 object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-432" />
            </div>
          </div>

          {/* Schedule CTA */}
          <div className="bg-brand-hunter text-brand-offwhite p-10 rounded-sm text-center max-w-4xl mx-auto" data-id="element-433">
            <Calendar className="w-12 h-12 text-brand-leather mx-auto mb-6" data-id="element-434" />
            <h2 className="text-2xl md:text-3xl font-serif mb-4" data-id="element-435">
              Follow Facebook for Current Details
            </h2>
            <p className="text-lg text-brand-offwhite/85 mb-8 max-w-2xl mx-auto leading-relaxed" data-id="element-436">
              Event dates and details change. Call the store or follow Kjergaard
              Sports on Facebook for the most up-to-date information on Shotgun
              Demo Day, class schedules, and store events.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-437">
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="bg-brand-leather hover:bg-brand-wood text-brand-offwhite px-8 py-3 rounded-sm font-medium transition-colors inline-flex items-center justify-center" data-id="element-438">
                <Facebook className="w-5 h-5 mr-2" data-id="element-439" />
                Follow on Facebook
              </a>
              <a href={PHONE_TEL} className="border border-brand-offwhite/40 hover:border-brand-offwhite text-brand-offwhite px-8 py-3 rounded-sm font-medium transition-colors inline-flex items-center justify-center" data-id="element-440">
                <Phone className="w-5 h-5 mr-2" data-id="element-441" />
                Call {PHONE}
              </a>
            </div>

            <div className="mt-8 pt-6 border-t border-brand-offwhite/15 flex items-start text-left max-w-2xl mx-auto" data-id="element-442">
              <Info className="w-5 h-5 text-brand-leather mr-3 flex-shrink-0 mt-0.5" data-id="element-443" />
              <p className="text-brand-offwhite/75 text-sm leading-relaxed" data-id="element-444">
                This website is for business information only. Kjergaard Sports
                does not conduct sales directly through this website. Event
                schedules and class dates change — please call or follow
                Facebook for current updates.
              </p>
            </div>
          </div>
        </div>
      </section>

      <PlanYourVisitCTA data-id="element-445" />
    </div>;
}